import React, {useEffect, useMemo, useRef, useState} from 'react';
import {
	EconomicProduct,
	ExportInvoiceModalProps,
	InvoiceEntry,
	InvoiceEntryRow,
	OnErrorType,
	QuickbookProductService,
	QuickbooksSettingId,
	TaxCode,
	XeroAccountItem,
} from './ExportInvoiceModal.type';
import {
	ButtonDropdown,
	ButtonDropdownField,
	Checkbox,
	ComboBox,
	DropdownList,
	FlexColumn,
	FlexRow,
	Heading,
	IconTooltip,
	Modal,
	SubHeading,
	Table,
	Text,
	Toggle,
} from '@forecast-it/design-system';
import {FormattedMessage, useIntl} from 'react-intl';
import XeroExportLinesTable from './XeroExportLinesTable';
import {
	applyXeroLineRows,
	carryXeroSelections,
	EMPTY_LINE_CONFIG,
	fetchXeroLineConfig,
	shouldUseXeroLinesTable,
	toXeroLineRows,
	XeroLineConfig,
} from './ExportInvoiceModal.xeroLines';
import {createFragmentContainer} from 'react-relay';
import {exportInvoiceModalFragment} from './ExportInvoiceModal.query';
import Util from '../../../forecast-app/shared/util/util';
import {getNotNullishNodes} from '../../../forecast-app/shared/util/NotNullPredicate';
import {hasPermission} from '../../../forecast-app/shared/util/PermissionsUtil';
import {PERMISSION_TYPE} from '../../../Permissions';
import {fetchXeroAccounts, onExportXeroInvoice} from './ExportInvoiceModal.xero';
import {hideLoader, showLoader} from '../../global_loader';
import {INVOICE_INTEGRATIONS} from '../../../forecast-app/admin-tab/finance-page/InvoiceIntegrationsUtils';
import {
	exportInvoiceToQuickbooksOnline,
	fetchQuickbooksData,
	getInitialQuickbookSettings,
	getQuickbooksLineDescription,
	toggleQuickbookSetting,
} from './ExportInvoiceModal.quickbooks';
import {isQboInvoicingImprovementsEnabled} from '../../../forecast-app/shared/util/FeatureUtil';
import {exportInvoiceToEconomics, fetchEconomicProducts} from './ExportInvoiceModal.economic';
import {ExportTarget, getTargetIntegration, QuickbooksCustomerOption} from './ExportInvoiceModal.target';

// The legacy window's Client heading, which names its search field. One export window is open at a time.
const CLIENT_LABEL_ID = 'export-invoice-client-label';

const ExportInvoiceModal = ({closeModal, viewer, quickbooksCustomers}: ExportInvoiceModalProps): JSX.Element => {
	const intl = useIntl();
	const {formatMessage} = intl;

	const noSpace = true;

	const {invoice, billTo} = viewer;
	const {client, currency} = invoice || {};

	const currencySymbol = useMemo(() => Util.GetCurrencySymbol(currency), [invoice?.currency]);

	// The legacy window preselects the invoice's client when it is QuickBooks-linked - which is
	// exactly when it is in this list, since ExportInvoiceButton builds it from linked clients.
	const [selectedClientId, setSelectedClientId] = useState<string | null>(
		() => quickbooksCustomers?.find(option => option.value === invoice?.client?.id)?.value ?? null
	);
	const selectedClient = quickbooksCustomers?.find(option => option.value === selectedClientId) ?? null;

	const exportTarget: ExportTarget | null = useMemo(() => {
		if (billTo) return {kind: 'BILL_TO', billTo};
		if (quickbooksCustomers) {
			return {
				kind: 'CLIENT',
				integration: INVOICE_INTEGRATIONS.QUICKBOOKS,
				clientId: selectedClient?.value ?? null,
				realmId: selectedClient?.realmId ?? null,
			};
		}
		return null;
	}, [billTo, quickbooksCustomers, selectedClient]);
	const targetIntegration = getTargetIntegration(exportTarget);

	const mapInvoiceEntryToRow = (
		invoiceEntry: InvoiceEntry,
		xeroAccount?: XeroAccountItem | null,
		quickbookProductService?: QuickbookProductService | null,
		economicProduct?: EconomicProduct | null,
		previewQuickbooksDescription = false
	): InvoiceEntryRow => {
		const taxRate = xeroAccount?.taxRate || 0;

		const row: InvoiceEntryRow = {
			...invoiceEntry,
			// The entry's own price, for restoring the line when a product is cleared.
			originalUnitPrice: invoiceEntry.unitPrice,
			projectColor: invoiceEntry.project?.projectColor || '',
			projectId: `P${invoiceEntry.project?.companyProjectId}`,
			customProjectId: invoiceEntry.project?.customProjectId,
			entryDescription: invoiceEntry.description,
			discountPercentage: (invoiceEntry.discount || 0) / 100,
			tax: taxRate,
			taxPercentage: taxRate / 100,
			totalPrice: Util.getInvoiceEntryAmount(
				invoiceEntry.unitPrice,
				invoiceEntry.quantity,
				invoiceEntry.discount,
				taxRate
			),
		};

		if (xeroAccount) {
			row.label = xeroAccount.label;
			row.xeroAccount = xeroAccount.value;
			row.accountCode = xeroAccount.code;
		}

		if (quickbookProductService) {
			row.label = quickbookProductService.label;
			// Under the flag the pick no longer overwrites the line's own description: the
			// description column previews what QuickBooks will receive, from the settings.
			if (previewQuickbooksDescription) {
				row.quickbooksDescription = quickbookProductService.description;
			} else {
				row.entryDescription = quickbookProductService.description;
			}
			row.quickbooksItem = quickbookProductService.value;
			row.quickbooksTaxable = quickbookProductService.taxable;
			row.quickbooksUnitPrice = quickbookProductService?.unitPrice;
		}

		if (economicProduct) {
			row.label = economicProduct.label;
			row.economicProduct = economicProduct.value;
		}

		return row;
	};

	const [loading, setLoading] = useState<boolean>(true);
	const [isReadyForExport, setIsReadyForExport] = useState<boolean>(false);

	useEffect(() => {
		if (loading) {
			showLoader();
		} else {
			hideLoader();
		}
	}, [loading]);

	// INTEGRATION
	const isXeroInvoice = useMemo(() => targetIntegration === INVOICE_INTEGRATIONS.XERO, [targetIntegration]);

	/*
	 * Resolved once per render. The ticket asks for the check "at the point where the invoice line
	 * decides which path to call"; scattering the call made it four separate decisions.
	 */
	const useLinesTable = shouldUseXeroLinesTable(isXeroInvoice);
	const isQuickbooksInvoice = useMemo(() => targetIntegration === INVOICE_INTEGRATIONS.QUICKBOOKS, [targetIntegration]);
	/*
	 * T4-149's new QuickBooks body, read once per render like useLinesTable: two reads in one
	 * interaction could disagree. Off, the pathways window is exactly today's.
	 */
	const qboNewBody = isQuickbooksInvoice && isQboInvoicingImprovementsEnabled();
	const isEconomicInvoice = useMemo(() => targetIntegration === INVOICE_INTEGRATIONS.ECONOMIC, [targetIntegration]);

	// INVOICE
	const initialInvoiceEntries = getNotNullishNodes(invoice?.entries?.edges);
	const [invoiceRows, setInvoiceRows] = useState<InvoiceEntryRow[]>(
		() => initialInvoiceEntries.map(entry => mapInvoiceEntryToRow(entry)) || []
	);

	// XERO
	const [projectInDescription, setProjectInDescription] = useState<boolean>(false);
	const [selectedXeroAccount, setSelectedXeroAccount] = useState<XeroAccountItem | null>(null);
	const [xeroAccounts, setXeroAccounts] = useState<XeroAccountItem[]>([]);
	const [xeroLineConfig, setXeroLineConfig] = useState<XeroLineConfig>(EMPTY_LINE_CONFIG);
	const requestedTenant = useRef<string | undefined>(undefined);
	const [xeroLineConfigLoading, setXeroLineConfigLoading] = useState<boolean>(false);
	/*
	 * Distinct from `loading`, which also covers the initial fetches. The line table must stop
	 * accepting changes while an export is in flight - but it must not be frozen by
	 * fetchXeroAccounts, a request it does not use: that one feeds the invoice-level dropdown,
	 * which is hidden under the flag, and it has no catch, so a rejection would leave the table
	 * permanently inert.
	 */
	const [saving, setSaving] = useState<boolean>(false);
	/*
	 * Why the reason is rendered INSIDE this modal rather than pushed as a MODAL_TYPE.WARNING.
	 *
	 * This modal is a design-system `Modal`, which Radix portals to document.body with a z-index of
	 * 1000, sets `document.body.style.pointerEvents = 'none'` around, and `aria-hidden`s the rest of
	 * the tree under. The legacy warning renders inside that tree at z-index 900, so it is covered,
	 * unclickable and invisible to a screen reader - the user sees the spinner stop and nothing else.
	 *
	 * `onError` a few lines down has always known this: "Old error modal is shown behind new
	 * design-system modals - so we have to close it first". Closing is what the modal used to do on
	 * failure, and what this change deliberately stopped doing so the user keeps their picks. Keeping
	 * the modal open therefore means the reason has to come with it.
	 */
	const [exportError, setExportError] = useState<string | null>(null);

	// QUICKBOOKS
	const [quickbooksTaxCodes, setQuickbooksTaxCodes] = useState<TaxCode[]>([]);
	const [selectedQuickbooksCode, setSelectedQuickbooksCode] = useState<TaxCode | null>(null);
	const [quickbooksProductsServices, setQuickbooksProductsServices] = useState<QuickbookProductService[]>([]);
	const [selectedQuickbooksProductService, setSelectedQuickbooksProductService] = useState<QuickbookProductService | null>(
		null
	);
	/*
	 * Which list failed to load for the company now showing. A failed list is empty, and without these
	 * it would read like a company with nothing in it: "No tax", exported untaxed. Not gated on the
	 * flag - they apply to today's window too.
	 */
	const [quickbooksTaxCodesFailed, setQuickbooksTaxCodesFailed] = useState<boolean>(false);
	const [quickbooksProductsFailed, setQuickbooksProductsFailed] = useState<boolean>(false);
	const [quickbookSettingsCount, setQuickbookSettingsCount] = useState<number>(0);
	const [quickbookSettings, setQuickbookSettings] = useState(getInitialQuickbookSettings(intl));
	const defaultTaxChecked = quickbookSettings.some(s => s.id === QuickbooksSettingId.USE_CUSTOMER_DEFAULT_TAX && s.checked);
	const customerDefaultTaxOn = qboNewBody && defaultTaxChecked;
	// With customer default tax on the code is not sent as the invoice's tax, so its list is not needed.
	const exportBlockedByTaxCodes = isQuickbooksInvoice && quickbooksTaxCodesFailed && !defaultTaxChecked;

	/*
	 * Display only: the export sends entryMap and the tax code id, never descriptions or amounts, so
	 * this cannot change what is exported.
	 *
	 * Tax follows the legacy window (quickbooks_export_modal.js:223): the chosen code's rate on a
	 * taxable line, 0 on any other. The rate is a percentage (QuickBooks' RateValue, e.g. 5), the unit
	 * Util.getInvoiceEntryAmount and Util.getInvoiceTotals read `tax` in. Customer default tax resets
	 * the code to '-1', whose rate is 0, so it previews no tax - as the legacy window did.
	 */
	const tableRows = useMemo(() => {
		if (!qboNewBody) return invoiceRows;
		const codeRate = selectedQuickbooksCode?.taxRate || 0;
		return invoiceRows.map(row => {
			const tax = row.quickbooksTaxable ? codeRate : 0;
			return {
				...row,
				entryDescription: getQuickbooksLineDescription(row, quickbookSettings),
				tax,
				taxPercentage: tax / 100,
				totalPrice: Util.getInvoiceEntryAmount(row.unitPrice, row.quantity, row.discount, tax),
			};
		});
	}, [qboNewBody, invoiceRows, quickbookSettings, selectedQuickbooksCode]);
	const invoiceTotals = useMemo(() => Util.getInvoiceTotals(tableRows), [tableRows]);

	// E-CONOMIC
	const [economicProducts, setEconomicProducts] = useState<EconomicProduct[]>([]);
	const [selectedEconomicProduct, setSelectedEconomicProduct] = useState<EconomicProduct | null>(null);

	const dropdownColumnLabel = useMemo(() => {
		if (isXeroInvoice) {
			return formatMessage({id: 'integrations.xero.xero_account'});
		}

		if (isQuickbooksInvoice) {
			return formatMessage({id: 'integrations.quickbooks.item_or_service'});
		}

		if (isEconomicInvoice) {
			return formatMessage({id: 'integrations.economic.product'});
		}

		return 'default';
	}, [isXeroInvoice, isQuickbooksInvoice, isEconomicInvoice]);

	useEffect(() => {
		setQuickbookSettingsCount(quickbookSettings.filter(item => item.checked).length);
	}, [quickbookSettings]);

	useEffect(() => {
		/*
		 * A merge, not a plain rebuild. This runs whenever the shared "apply to all lines" account
		 * dropdown changes, and it rebuilds from initialInvoiceEntries - the raw Relay entries -
		 * which carry none of the line's own Xero selections. Rebuilding outright would clear the
		 * Item and tracking values on every line. The account and its tax are deliberately still
		 * overwritten: that is what the shared dropdown is for.
		 */
		setInvoiceRows(previousRows =>
			carryXeroSelections(
				initialInvoiceEntries.map(entry =>
					mapInvoiceEntryToRow(
						entry,
						selectedXeroAccount,
						selectedQuickbooksProductService,
						selectedEconomicProduct,
						qboNewBody
					)
				) || [],
				previousRows
			)
		);
	}, [selectedXeroAccount, selectedQuickbooksProductService, selectedEconomicProduct]);

	useEffect(() => {
		if (billTo) {
			setLoading(true);

			if (isXeroInvoice) {
				/*
				 * Only off the flag. `fetchXeroAccounts` uses the pre-flag route and feeds the
				 * invoice-level dropdown, which is hidden under the flag - so under it this was a
				 * live Xero call on every modal open, feeding a control nobody sees, while still
				 * being the only thing that cleared `loading`. `getXeroAccounts` calls `.json()`
				 * and `.map` on any non-403 response, so a 429 or a non-array body threw and left
				 * the modal loading for ever.
				 */
				if (!useLinesTable) {
					fetchXeroAccounts(billTo, (accounts: XeroAccountItem[]) => {
						setXeroAccounts(accounts);
						setLoading(false);
					});
				}

				if (useLinesTable) {
					// Stamped with the organisation asked for: the effect reruns on billTo, and an
					// earlier answer landing last would show one organisation's configuration
					// while the export posts to another.
					const tenantId = billTo.billFrom?.externalId;
					requestedTenant.current = tenantId;
					setXeroLineConfig(EMPTY_LINE_CONFIG);
					setXeroLineConfigLoading(true);
					fetchXeroLineConfig(tenantId).then(config => {
						/*
						 * Inside the guard, so a response for an organisation the user has moved
						 * away from cannot open the modal for the one now loading.
						 * `fetchXeroLineConfig` never rejects, so the current request always
						 * resolves and always reaches this.
						 */
						if (requestedTenant.current === tenantId) {
							setXeroLineConfig(config);
							setXeroLineConfigLoading(false);
							// Under the flag this read replaces fetchXeroAccounts as what opens
							// the modal, so it is what clears `loading`.
							setLoading(false);
						}
					});
				}
			} else if (isQuickbooksInvoice) {
				setQuickbooksTaxCodesFailed(false);
				setQuickbooksProductsFailed(false);
				fetchQuickbooksData(
					(taxCodes: TaxCode[]) => setQuickbooksTaxCodes(taxCodes),
					(taxCode: TaxCode | null) => setSelectedQuickbooksCode(taxCode),
					(productsServices: QuickbookProductService[]) => setQuickbooksProductsServices(productsServices),
					({taxCodesFailed, productsFailed}) => {
						setQuickbooksTaxCodesFailed(taxCodesFailed);
						setQuickbooksProductsFailed(productsFailed);
						setLoading(false);
					},
					billTo.billFrom?.externalId || null
				);
			} else if (isEconomicInvoice) {
				fetchEconomicProducts(products => {
					setEconomicProducts(products);
					setLoading(false);
				});
			}
		}
	}, [billTo]);

	/*
	 * The legacy window's data load. Keyed on the realm, not the client: tax codes and products
	 * belong to a QuickBooks company, so moving between two clients of one realm keeps every pick,
	 * and moving to another realm starts the lines over - an item from realm A means nothing in B.
	 * Null while no client is chosen.
	 */
	const clientRealmKey = exportTarget?.kind === 'CLIENT' && exportTarget.clientId ? exportTarget.realmId ?? '' : null;
	/*
	 * One number per load, not the realm: A -> B -> A sends two reads of realm A, and comparing realms
	 * would let the first answer through while the second is still out - clearing the loader, then
	 * replacing a code the user picked in the meantime.
	 */
	const requestSeq = useRef(0);

	useEffect(() => {
		if (exportTarget?.kind !== 'CLIENT') return;
		// Bumped here too, so a read still out when the client is cleared lands on nothing.
		const seq = ++requestSeq.current;
		if (clientRealmKey === null) {
			setLoading(false);
			return;
		}
		setLoading(true);
		setSelectedQuickbooksProductService(null);
		// Cleared here, not only by the load: a failed tax-code read sets no code, and the last
		// realm's code must not be exported to this one.
		setSelectedQuickbooksCode(null);
		setInvoiceRows(initialInvoiceEntries.map(entry => mapInvoiceEntryToRow(entry)));
		// The last company's lists too: its item ids are small per-company integers, so one picked
		// while this company loads could export as a different product here.
		setQuickbooksTaxCodes([]);
		setQuickbooksProductsServices([]);
		setQuickbooksTaxCodesFailed(false);
		setQuickbooksProductsFailed(false);
		// Each setter checks it still answers the latest load: a slow answer from one the user moved
		// away from must not overwrite the one now showing.
		const isCurrent = () => requestSeq.current === seq;
		fetchQuickbooksData(
			taxCodes => {
				if (isCurrent()) setQuickbooksTaxCodes(taxCodes);
			},
			taxCode => {
				if (isCurrent()) setSelectedQuickbooksCode(taxCode);
			},
			productsServices => {
				if (isCurrent()) setQuickbooksProductsServices(productsServices);
			},
			({taxCodesFailed, productsFailed}) => {
				if (!isCurrent()) return;
				setQuickbooksTaxCodesFailed(taxCodesFailed);
				setQuickbooksProductsFailed(productsFailed);
				setLoading(false);
			},
			clientRealmKey || null
		);
	}, [clientRealmKey]);

	const getMappedInvoiceRows = (
		row,
		xeroAccount: XeroAccountItem | null,
		productService: QuickbookProductService | null,
		economicProduct: EconomicProduct | null
	) => {
		return (
			invoiceRows.map(entry => {
				if (entry.id === row.id) {
					return mapInvoiceEntryToRow(entry, xeroAccount, productService, economicProduct, qboNewBody);
				}

				return entry;
			}) || []
		);
	};

	const selectExpenseXeroAccount = (row, xeroAccount: XeroAccountItem) => {
		setInvoiceRows(getMappedInvoiceRows(row, xeroAccount, null, null));
	};

	const selectQuickBooksProductService = (row, productService: QuickbookProductService) => {
		setInvoiceRows(getMappedInvoiceRows(row, null, productService, null));
	};

	const selectEconomicProduct = (row, product: EconomicProduct) => {
		setInvoiceRows(getMappedInvoiceRows(row, null, null, product));
	};

	useEffect(() => {
		// The legacy window has nowhere to export to until a client is chosen.
		const hasTarget = !(exportTarget?.kind === 'CLIENT' && !exportTarget.clientId);
		setIsReadyForExport(
			hasTarget &&
				invoiceRows.every(row => {
					if (isXeroInvoice) {
						return !!row.xeroAccount;
					}

					if (isQuickbooksInvoice) {
						return !!row.quickbooksItem;
					}

					if (isEconomicInvoice) {
						return !!row.economicProduct;
					}

					return false;
				})
		);
	}, [invoiceRows, exportTarget]);

	const onSave = () => {
		if (!invoice || !exportTarget) return;
		if (exportTarget.kind === 'CLIENT' && !exportTarget.clientId) return;
		setLoading(true);
		setSaving(true);
		setExportError(null);

		const onSuccess = () => {
			setLoading(false);
			setSaving(false);
			closeModal();
		};

		/*
		 * A finished export that FAILED: stop the spinner, but leave the modal open.
		 *
		 * Closing on failure discards every item, account, tax and tracking pick the user made,
		 * with no way back but doing it all again - and the failures are ordinary and
		 * recoverable: an archived Xero contact, an invoice already linked, a token that needs
		 * reconnecting. The export writes nothing to the invoice unless Xero accepted it, so
		 * the modal the user is looking at is still exactly what they would retry.
		 */
		const onExportFailed = (reason?: string | null) => {
			setLoading(false);
			setSaving(false);
			// Null when the service gave nothing a user can act on - see exportFailureMessage.
			setExportError(reason || formatMessage({id: 'invoicing.not_exported'}));
		};

		const onError: OnErrorType = (error, onFailure) => {
			// Workaround. Old error modal is shown behind new design-system modals - so we have to close it first.
			onSuccess();
			onFailure(error);
		};
		if (isXeroInvoice && billTo) {
			onExportXeroInvoice({
				intl,
				onSuccess,
				onExportFailed,
				billTo,
				projectInDescription,
				invoiceRows,
				invoice,
				onError,
				useLinesTable,
			});
		} else if (isQuickbooksInvoice) {
			exportInvoiceToQuickbooksOnline({
				intl,
				onSuccess,
				target: exportTarget,
				invoice,
				invoiceRows,
				quickbookSettings,
				taxCode: selectedQuickbooksCode,
				onError,
			});
		} else if (isEconomicInvoice && billTo) {
			exportInvoiceToEconomics({
				intl,
				onSuccess,
				billTo,
				invoice,
				invoiceRows,
				onError,
			});
		}
	};

	const getProjectIdRenderProps = row => {
		return {
			color: row.projectColor,
			text: row.customProjectId,
		};
	};

	const getExportToName = () => {
		if (isXeroInvoice) {
			return 'Xero';
		}

		if (isQuickbooksInvoice) {
			return 'QuickBooks Online';
		}

		if (isEconomicInvoice) {
			return 'E-conomic';
		}

		return '';
	};

	const onQuickbookSettingChange = (id: QuickbooksSettingId) => {
		setQuickbookSettings(toggleQuickbookSetting(quickbookSettings, id, qboNewBody));
		// The legacy window resets the tax code on every flip of customer default tax, either way.
		if (qboNewBody && id === QuickbooksSettingId.USE_CUSTOMER_DEFAULT_TAX) {
			setSelectedQuickbooksCode(quickbooksTaxCodes.find(taxCode => taxCode.value === '-1') || null);
		}
	};

	const getDropdownColumnItems = () => {
		if (isXeroInvoice) {
			return xeroAccounts;
		}

		if (isQuickbooksInvoice) {
			return quickbooksProductsServices;
		}

		if (isEconomicInvoice) {
			return economicProducts;
		}

		return [];
	};

	/*
	 * Why Export is disabled, when it is disabled because Xero already holds the invoice.
	 *
	 * The readiness tooltip below answers "what do I still have to do"; this answers "why can I not
	 * try again", which is a different question and the one a user hits after a half-updated export.
	 * Without it the button simply goes dead with nothing said. Xero only - the gate reads
	 * `invoice.xeroId`, and QuickBooks and e-conomic have their own ids and their own modals' rules.
	 */
	const alreadyExportedTooltipContent =
		isXeroInvoice && invoice?.xeroId ? (
			<FlexColumn>
				<SubHeading>
					<FormattedMessage id={'invoicing.xero.export_done_tooltip.title'} />
				</SubHeading>
				<Text>
					<FormattedMessage id={'invoicing.xero.export_done_tooltip.description'} />
				</Text>
			</FlexColumn>
		) : undefined;

	// The legacy window with no client chosen: the client is the missing step, not a product.
	const awaitingClientTooltipContent =
		exportTarget?.kind === 'CLIENT' && !exportTarget.clientId ? (
			<SubHeading>
				<FormattedMessage id={'settings_finance.select_client'} />
			</SubHeading>
		) : undefined;

	// A QuickBooks list that failed: say that, rather than ask for a pick the user cannot make.
	const failedListTooltipContent = exportBlockedByTaxCodes ? (
		<SubHeading>
			<FormattedMessage id={'invoicing.quickbooks.tax_codes_load_failed'} />
		</SubHeading>
	) : !isReadyForExport && isQuickbooksInvoice && quickbooksProductsFailed ? (
		<SubHeading>
			<FormattedMessage id={'invoicing.quickbooks.products_load_failed'} />
		</SubHeading>
	) : undefined;

	const readinessTooltipContent = isReadyForExport
		? failedListTooltipContent
		: awaitingClientTooltipContent ??
		  failedListTooltipContent ?? (
				<FlexColumn>
					<SubHeading>
						<FormattedMessage
							id={
								isXeroInvoice
									? 'invoicing.xero.export_disabled_tooltip.title'
									: isEconomicInvoice
									? 'invoicing.economic.export_disabled_tooltip.title'
									: 'invoicing.quickbooks.export_disabled_tooltip.title'
							}
						/>
					</SubHeading>
					<Text>
						<FormattedMessage
							id={
								isXeroInvoice
									? 'invoicing.xero.export_disabled_tooltip.description'
									: isEconomicInvoice
									? 'invoicing.economic.export_disabled_tooltip.description'
									: 'invoicing.quickbooks.export_disabled_tooltip.description'
							}
						/>
					</Text>
				</FlexColumn>
		  );

	// The Xero link wins: it is the reason the button is dead even on a line that IS ready.
	const disabledTooltipContent = alreadyExportedTooltipContent ?? readinessTooltipContent;
	return (
		<Modal size={Modal.SIZE.EXTRA_EXTRA_LARGE} onCloseModal={closeModal}>
			<Modal.Title>
				<Heading size={4}>
					<FormattedMessage id={'invoicing.export_to'} values={{systemName: getExportToName()}} />
				</Heading>
			</Modal.Title>

			{/*
			 * Outside `Modal.Content`, deliberately.
			 *
			 * That element is the scroll container - `max-height: calc(100vh - 300px); overflow-y:
			 * auto` - while the Export button sits below it in the footer. As the first child inside
			 * it, the reason was off-screen for the user most likely to need it: the one who scrolled
			 * a long line table to reach Export. They saw the spinner stop and nothing else, which is
			 * the symptom this whole change exists to remove.
			 *
			 * The padding matches `globalSpacing['600']` (24px), which `Modal.Title` and
			 * `Modal.Content` both use, so the reason lines up with the dialog's other text. The token
			 * is not exported from the design system's package root, or it would be used by name.
			 */}
			{exportError ? (
				<div
					data-cy={'export-failure-reason'}
					role={'alert'}
					/*
					 * Scrolls rather than growing: the reason comes from Xero and has no length limit,
					 * and this sits OUTSIDE the content's own scroll area, so an unbounded one pushes
					 * the dialog past the viewport and takes the footer buttons with it.
					 *
					 * `min(20vh, 120px)`, not `20vh`. `Modal.Content` already claims
					 * `calc(100vh - 300px)`, leaving roughly 150px for the title, this and the footer -
					 * so on anything taller than about 750px a flat 20vh is more than the room left.
					 * Measured in Chromium: at 1366x768 the bottom of Export landed at 769px, and at
					 * 1920x1080 the dialog lost its top 58px.
					 */
					style={{padding: '0 24px', maxHeight: 'min(20vh, 120px)', overflowY: 'auto'}}
				>
					<Text color={'error'}>{exportError}</Text>
				</div>
			) : null}

			<Modal.Content>
				<FlexColumn gap={'xxl'}>
					<FlexRow gap={'l'} alignItems={'start'}>
						{exportTarget?.kind === 'CLIENT' ? (
							<FlexColumn>
								{/*
								 * Searchable, as the old window's client picker was: a company can have hundreds of
								 * linked clients, and typing filters by substring.
								 *
								 * `saving`, not `loading`: a slow company must not trap the user on the client they
								 * are moving away from. The per-load check in the load effect is what makes switching
								 * mid-load safe.
								 */}
								<SubHeading>
									<span id={CLIENT_LABEL_ID}>
										<FormattedMessage id={'common.client'} />
									</span>
								</SubHeading>
								<ComboBox
									aria-labelledby={CLIENT_LABEL_ID}
									defaultItems={quickbooksCustomers ?? []}
									value={selectedClientId}
									// Null when the text is cleared; the window has no "no client" choice.
									onChange={(key: string | null) => {
										if (key != null) setSelectedClientId(String(key));
									}}
									disabled={saving}
									placeholder={
										formatMessage({id: 'common.select'}) + ' ' + formatMessage({id: 'common.client'})
									}
									helperText={
										(quickbooksCustomers?.length ?? 0) === 0
											? formatMessage({id: 'invoicing.quickbooks.no_linked_clients'})
											: undefined
									}
								>
									{(option: QuickbooksCustomerOption) => (
										<ComboBox.Item key={option.value} textValue={option.label}>
											<Text>{option.label}</Text>
										</ComboBox.Item>
									)}
								</ComboBox>
							</FlexColumn>
						) : (
							<>
								<FlexColumn>
									<SubHeading>
										<FormattedMessage id={'common.client'} />
									</SubHeading>

									<Text ellipsis>{client?.name}</Text>
								</FlexColumn>

								<FlexColumn>
									<FlexRow alignItems={'center'}>
										<SubHeading>
											<FormattedMessage id={'settings_finance.bill_from_entity'} />
										</SubHeading>

										<IconTooltip hidden>
											<IconTooltip.Item
												title={formatMessage({id: 'settings_finance.bill_from_entity'})}
												details={formatMessage({
													id: 'invoicing.export.bill_from_entity_tooltip_details',
												})}
											/>
										</IconTooltip>
									</FlexRow>

									<Text ellipsis>{billTo?.billFrom?.name}</Text>
								</FlexColumn>

								<FlexColumn>
									<FlexRow alignItems={'center'}>
										<SubHeading>
											<FormattedMessage id={'settings_finance.bill_to_entity'} />
										</SubHeading>

										<IconTooltip>
											<IconTooltip.Item
												title={formatMessage({id: 'settings_finance.bill_to_entity'})}
												details={formatMessage({id: 'invoicing.export.bill_to_entity_tooltip_details'})}
											/>
										</IconTooltip>
									</FlexRow>

									<Text ellipsis>{billTo?.name}</Text>
								</FlexColumn>
							</>
						)}

						{/*
						 * Hidden under the flag: the line's own Account column replaces it, and the design
						 * drops the invoice-level picker. Leaving it on screen would be actively unsafe -
						 * it is fed by the pre-flag route, which returns EVERY account class unfiltered, so
						 * an expense account picked here satisfies the Export gate (!!row.xeroAccount) while
						 * the filtered column cannot resolve it and every line reads "Select account".
						 */}
						{isXeroInvoice && !useLinesTable && (
							<FlexColumn>
								<SubHeading>
									<FormattedMessage id={'integrations.xero.xero_account'} />

									<DropdownList>
										<DropdownList.Trigger>
											<ButtonDropdownField disabled={loading} isDefaultLabel={!selectedXeroAccount}>
												{selectedXeroAccount
													? selectedXeroAccount.label
													: formatMessage({id: 'common.select'}) +
													  ' ' +
													  formatMessage({id: 'integrations.xero.xero_account'})}
											</ButtonDropdownField>
										</DropdownList.Trigger>
										{xeroAccounts?.length > 0 && (
											<DropdownList.Content offset={10}>
												{xeroAccounts.map((xeroAccount, index) => (
													<DropdownList.Item
														key={`xero-${index}`}
														onClick={() => setSelectedXeroAccount(xeroAccount)}
													>
														{xeroAccount.label}
													</DropdownList.Item>
												))}
											</DropdownList.Content>
										)}
									</DropdownList>
								</SubHeading>
							</FlexColumn>
						)}

						{isQuickbooksInvoice && (
							<>
								<FlexColumn>
									<SubHeading>
										<FormattedMessage id={'integrations.quickbooks.tax_code'} />

										<DropdownList>
											<DropdownList.Trigger>
												<ButtonDropdownField
													disabled={loading || customerDefaultTaxOn}
													isDefaultLabel={customerDefaultTaxOn || !selectedQuickbooksCode}
												>
													{customerDefaultTaxOn
														? formatMessage({id: 'invoicing.quickbooks.customer_default_tax'})
														: quickbooksTaxCodesFailed
														? formatMessage({id: 'invoicing.quickbooks.tax_codes_load_failed'})
														: selectedQuickbooksCode
														? selectedQuickbooksCode.label
														: formatMessage({id: 'invoicing.no_tax'})}
												</ButtonDropdownField>
											</DropdownList.Trigger>
											{quickbooksTaxCodes?.length > 0 && (
												<DropdownList.Content offset={10}>
													{quickbooksTaxCodes.map((taxCode, index) => (
														<DropdownList.Item
															key={`tax-code-${index}`}
															onClick={() => setSelectedQuickbooksCode(taxCode)}
														>
															{taxCode.label}
														</DropdownList.Item>
													))}
												</DropdownList.Content>
											)}
										</DropdownList>
									</SubHeading>
								</FlexColumn>

								<FlexColumn>
									<SubHeading>
										<FormattedMessage id={'integrations.quickbooks.items_and_services'} />

										<DropdownList>
											<DropdownList.Trigger>
												<ButtonDropdownField
													disabled={loading}
													width={'320px'}
													isDefaultLabel={!selectedQuickbooksProductService}
												>
													{selectedQuickbooksProductService
														? selectedQuickbooksProductService.label
														: quickbooksProductsFailed
														? formatMessage({id: 'invoicing.quickbooks.products_load_failed'})
														: formatMessage({id: 'common.select'}) +
														  ' ' +
														  formatMessage({id: 'integrations.quickbooks.items_and_services'})}
												</ButtonDropdownField>
											</DropdownList.Trigger>
											{quickbooksProductsServices?.length > 0 && (
												<DropdownList.Content offset={10}>
													{quickbooksProductsServices.map((productService, index) => (
														<DropdownList.Item
															key={`product-service-${index}`}
															onClick={() => setSelectedQuickbooksProductService(productService)}
														>
															{productService.label}
														</DropdownList.Item>
													))}
												</DropdownList.Content>
											)}
										</DropdownList>
									</SubHeading>
								</FlexColumn>
							</>
						)}

						{isEconomicInvoice && (
							<FlexColumn>
								<SubHeading>
									<FormattedMessage id={'integrations.economic.product'} />

									<DropdownList>
										<DropdownList.Trigger>
											<ButtonDropdownField disabled={loading} isDefaultLabel={!selectedEconomicProduct}>
												{selectedEconomicProduct
													? selectedEconomicProduct.label
													: formatMessage({id: 'common.select'}) +
													  ' ' +
													  formatMessage({id: 'integrations.economic.product'})}
											</ButtonDropdownField>
										</DropdownList.Trigger>
										{economicProducts?.length > 0 && (
											<DropdownList.Content offset={10}>
												{economicProducts.map((product, index) => (
													<DropdownList.Item
														key={`economic-product-${index}`}
														onClick={() => setSelectedEconomicProduct(product)}
													>
														{product.label}
													</DropdownList.Item>
												))}
											</DropdownList.Content>
										)}
									</DropdownList>
								</SubHeading>
							</FlexColumn>
						)}
					</FlexRow>

					{isQuickbooksInvoice && !qboNewBody && (
						<FlexRow>
							<DropdownList>
								<DropdownList.Trigger>
									<ButtonDropdown emphasis={'medium'} style={{width: 'fit-content'}}>
										<FormattedMessage
											id={'invoicing.quickbooks.quickbook_settings'}
											values={{count: quickbookSettingsCount}}
										/>
									</ButtonDropdown>
								</DropdownList.Trigger>
								<DropdownList.Content offset={10} align={'start'}>
									{quickbookSettings.map(setting => (
										<DropdownList.CheckboxItem
											key={`quickbook-setting-${setting.id}`}
											onChange={() => onQuickbookSettingChange(setting.id)}
											checked={setting.checked}
											disabled={setting.disabled}
										>
											{setting.name}
										</DropdownList.CheckboxItem>
									))}
								</DropdownList.Content>
							</DropdownList>
						</FlexRow>
					)}

					{qboNewBody && (
						<div style={{display: 'grid', gridTemplateColumns: 'repeat(5, minmax(0, 1fr))', gap: '20px'}}>
							{quickbookSettings.map(setting => (
								/*
								 * A named group, because the design-system Toggle hard-codes
								 * aria-label="toggle" on its switch and takes no label prop: without
								 * the group every switch is announced only as "toggle".
								 *
								 * The grid stretches every column to the tallest label, and space-between
								 * pins each switch to the bottom, so the five switches share one line
								 * however each label wraps.
								 */
								<div
									key={`quickbook-setting-${setting.id}`}
									role={'group'}
									aria-label={setting.name}
									style={{
										display: 'flex',
										flexDirection: 'column',
										justifyContent: 'space-between',
										gap: '8px',
									}}
								>
									<SubHeading size={'2'}>{setting.name}</SubHeading>
									<Toggle
										checked={setting.checked}
										onChange={() => onQuickbookSettingChange(setting.id)}
										disabled={setting.disabled || saving}
									/>
								</div>
							))}
						</div>
					)}

					{isXeroInvoice && (
						<Checkbox
							checked={projectInDescription}
							onChange={() => setProjectInDescription(!projectInDescription)}
							labelText={formatMessage({id: 'invoicing.xero.project_in_description'})}
							disabled={loading}
						/>
					)}

					{useLinesTable ? (
						<XeroExportLinesTable
							idHeader={formatMessage({id: 'common.id'})}
							rows={toXeroLineRows(invoiceRows)}
							currency={invoice?.currency || ''}
							accounts={xeroLineConfig.accounts}
							items={xeroLineConfig.items}
							trackingCategories={xeroLineConfig.trackingCategories}
							onRowsChange={changed => setInvoiceRows(rows => applyXeroLineRows(rows, changed))}
							disabled={saving}
							canRepriceLines={hasPermission(PERMISSION_TYPE.INVOICE_UPDATE)}
							loading={xeroLineConfigLoading}
							failed={xeroLineConfig.failed}
						/>
					) : (
						<Table data={tableRows} width={'100%'}>
							<Table.ColoredIdIconColumn
								accessorKey={'project.id'}
								header={formatMessage({id: 'common.id'})}
								renderProps={getProjectIdRenderProps}
							/>
							<Table.TextColumn accessorKey={'project.name'} header={formatMessage({id: 'common.project'})} />
							<Table.TextColumn
								accessorKey={'entryDescription'}
								width={'232px'}
								header={formatMessage({id: 'common.description'})}
							/>

							<Table.DropdownColumn
								header={dropdownColumnLabel}
								label={row => row.label || ''}
								defaultLabel={formatMessage({id: 'common.select'}) + ' ' + dropdownColumnLabel}
								width={'180px'}
							>
								{getDropdownColumnItems().map((item, index) => (
									<Table.DropdownColumn.ListItem
										key={`row-dropdown-${index}`}
										onClick={row => {
											if (isXeroInvoice) {
												selectExpenseXeroAccount(row, item);
											} else if (isQuickbooksInvoice) {
												selectQuickBooksProductService(row, item);
											} else if (isEconomicInvoice) {
												selectEconomicProduct(row, item);
											}
										}}
										disabled={loading}
									>
										{item.label}
									</Table.DropdownColumn.ListItem>
								))}
							</Table.DropdownColumn>

							<Table.NumberColumn accessorKey={'quantity'} header={formatMessage({id: 'common.quantity'})} />
							<Table.CurrencyColumn
								currency={invoice?.currency || ''}
								accessorKey={'unitPrice'}
								header={formatMessage({id: 'common.unit_price'})}
								maximumFractionDigits={4}
							/>
							<Table.PercentageColumn
								accessorKey={'discountPercentage'}
								header={formatMessage({id: 'project_section.xero_modal_table_discount'})}
							/>
							<Table.PercentageColumn accessorKey={'taxPercentage'} header={formatMessage({id: 'common.tax'})} />
							<Table.CurrencyColumn
								currency={invoice?.currency || ''}
								accessorKey={'totalPrice'}
								header={formatMessage({id: 'expense_item_modal.total_price'})}
							/>
						</Table>
					)}

					<FlexRow alignItems={'end'} alignSelf={'end'}>
						<FlexColumn gap={'s'}>
							<FlexRow gap={'xxl'} justifyContent={'space-between'}>
								<FormattedMessage id={'common.subtotal'} />:
								<Text>
									{Util.getFormattedNumberWithCurrency(currencySymbol, invoiceTotals.subtotal, intl, noSpace)}
								</Text>
							</FlexRow>

							<FlexRow gap={'xxl'} justifyContent={'space-between'}>
								<FormattedMessage id={'project_section.xero_modal_table_discount'} />:
								<Text>
									{Util.getFormattedNumberWithCurrency(currencySymbol, invoiceTotals.discount, intl, noSpace)}
								</Text>
							</FlexRow>

							<FlexRow gap={'xxl'} justifyContent={'space-between'}>
								<FormattedMessage id={'common.tax'} />:
								<Text>
									{Util.getFormattedNumberWithCurrency(currencySymbol, invoiceTotals.tax, intl, noSpace)}
								</Text>
							</FlexRow>

							<FlexRow gap={'xxl'} justifyContent={'space-between'}>
								<SubHeading>
									<FormattedMessage id={'common.total_amount'} />
								</SubHeading>
								<SubHeading>
									{Util.getFormattedNumberWithCurrency(currencySymbol, invoiceTotals.total, intl, noSpace)}
								</SubHeading>
							</FlexRow>
						</FlexColumn>
					</FlexRow>
				</FlexColumn>
			</Modal.Content>

			<Modal.Footer>
				{/*
				 * `invoice.xeroId` as well: the failure branch returns `invoiceId` so Relay refetches,
				 * and the failure that matters most is the half-updated one - Xero accepted, the link
				 * was written, and a later step threw. The modal stays open by design, so the user's
				 * picks survive, but a retry on an invoice Xero already holds is the one thing that
				 * must not be invited. The button that OPENS this modal is already hidden once the
				 * link exists, so nothing changes on the ordinary path.
				 */}
				<Modal.PrimaryFooterButton
					onPress={onSave}
					tooltipContent={disabledTooltipContent}
					disabled={!isReadyForExport || loading || !!invoice?.xeroId || exportBlockedByTaxCodes}
				>
					{formatMessage({id: 'common.export'})}
				</Modal.PrimaryFooterButton>

				<Modal.SecondaryFooterButton onPress={closeModal} disabled={loading}>
					{formatMessage({id: 'common.cancel'})}
				</Modal.SecondaryFooterButton>
			</Modal.Footer>
		</Modal>
	);
};

export default createFragmentContainer(ExportInvoiceModal, exportInvoiceModalFragment);
